import { useEffect, useRef, useState } from 'react';

/**
 * The platform clock, as a browser is allowed to know it.
 *
 * No surface may read `new Date()` to decide what the PLATFORM thinks the time
 * is. Two independent reasons, and each one has already produced a defect:
 *
 *  - a device's own clock is wrong. A counter tablet drifts, and a countdown
 *    anchored on it tells a baker to start the oven at the wrong minute
 *    (PRD BAK-004);
 *  - outside production the platform runs on the rehearsal clock (TST-003),
 *    whose offset can be hours ahead of wall time. Every browser on the machine
 *    still reads wall time, so a screen that dates itself from the browser is
 *    dating itself from nobody's clock: the admin dashboard opened on an empty
 *    board while 34 orders were in flight one day over, and printed the
 *    server's own time on the same line.
 *
 * So every response that a dated screen renders carries a `serverTime`, and
 * everything here works from the OFFSET between that stamp and the device clock
 * as it read when the stamp arrived. An offset, not the stamp itself: the stamp
 * is one frozen instant, and a screen anchored on it stops ticking, while an
 * offset keeps a second-resolution countdown running between responses.
 *
 * This is the one derivation of platform time in the product - the bakery
 * tablet and the guest storefront both read it from here.
 */

/** Offset to add to a device timestamp to get platform time, in milliseconds. */
export function serverOffsetMs(serverTime: string, deviceNowMs: number): number {
  const parsed = Date.parse(serverTime);
  if (Number.isNaN(parsed)) return 0;
  return parsed - deviceNowMs;
}

/** Platform time, as a millisecond timestamp. */
export function platformNowMs(deviceNowMs: number, offsetMs: number): number {
  return deviceNowMs + offsetMs;
}

/** Positive while the target is still ahead; negative once it has passed. */
export function remainingMs(target: string, deviceNowMs: number, offsetMs: number): number {
  const parsed = Date.parse(target);
  if (Number.isNaN(parsed)) return 0;
  return parsed - platformNowMs(deviceNowMs, offsetMs);
}

/** How often `usePlatformNow` re-renders when the caller names no cadence. */
export const PLATFORM_TICK_MS = 1_000;

/**
 * Platform now, ticking, from the last `serverTime` the screen was told.
 *
 * `serverTime` is whatever stamp the screen most recently received - a field on
 * the response it is rendering, so the "now" a screen reasons in is the same one
 * the server used to compute what it is showing. `null` while nothing has
 * answered yet, and the hook then returns the device clock unshifted: there is
 * nothing better to say, and a caller that must not guess should not call this
 * until it has a stamp.
 *
 * The offset is anchored the moment a NEW stamp arrives, against the device
 * clock as it reads then, and re-anchored on every later one - so a rehearsal
 * clock jumped mid-session is picked up by the next response rather than
 * needing a reload. Recomputing it on every tick instead would pin the screen
 * to that one instant for ever, because `deviceNow + (serverTime - deviceNow)`
 * is `serverTime` at any tick.
 */
export function usePlatformNow(serverTime: string | null, intervalMs = PLATFORM_TICK_MS): number {
  /*
   * The interval exists only to force a re-render. The instant itself is read
   * fresh below, so a render caused by anything else - a new response, a tap -
   * is just as current as one caused by the timer.
   */
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((count) => count + 1), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);

  /*
   * Anchored during render rather than in an effect, deliberately. An effect
   * runs after the first paint, so the first frame that had a stamp would still
   * be drawn on the device clock - which under a ten-hour rehearsal offset is a
   * visible flash of the wrong day on the very screen this exists to fix.
   */
  const anchor = useRef<{ readonly serverTime: string; readonly offsetMs: number } | null>(null);
  if (serverTime !== null && anchor.current?.serverTime !== serverTime) {
    anchor.current = { serverTime, offsetMs: serverOffsetMs(serverTime, Date.now()) };
  }

  return platformNowMs(Date.now(), anchor.current?.offsetMs ?? 0);
}
